import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ForumTopic, ForumRoom } from '../types';
import {
  IconPlus,
  IconPin,
  IconPinFilled,
  IconLock,
  IconLockOpen,
  IconTrash,
  IconPencil,
  IconMessages,
  IconArrowLeft,
} from '@tabler/icons-react';
import {
  listTopics,
  listRooms,
  toggleTopicPinned,
  toggleTopicLocked,
  deleteTopic,
  createTopic,
  updateTopic,
} from '../services/forumService';
import { useAuth } from '../contexts/AuthContext';
import TopicModal from '../components/TopicModal';

export default function ForumTopicsPage() {
  const { roomId: scopedRoomId } = useParams<{ roomId?: string }>();
  const isScoped = Boolean(scopedRoomId);
  const navigate = useNavigate();
  const { user } = useAuth();

  const [topics, setTopics] = useState<ForumTopic[]>([]);
  const [rooms, setRooms] = useState<ForumRoom[]>([]);
  const [scopedRoom, setScopedRoom] = useState<ForumRoom | null>(null);
  const [loading, setLoading] = useState(true);
  const [roomFilter, setRoomFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingTopic, setEditingTopic] = useState<ForumTopic | null>(null);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopedRoomId]);

  useEffect(() => {
    if (!loading) {
      loadTopics();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomFilter, searchQuery]);

  const loadData = async () => {
    try {
      setLoading(true);
      const roomsResult = await listRooms({ includeInactive: false });
      setRooms(roomsResult.rooms);
      if (isScoped && scopedRoomId) {
        const found = roomsResult.rooms.find(r => r.id === scopedRoomId) ?? null;
        setScopedRoom(found);
      }
      await loadTopics();
    } finally {
      setLoading(false);
    }
  };

  const loadTopics = async () => {
    try {
      const result = await listTopics({
        roomFilter: scopedRoomId ?? (roomFilter || undefined),
        searchQuery: searchQuery || undefined,
      });
      setTopics(result.topics);
    } catch (error) {
      console.error('Error loading topics:', error);
    }
  };

  const handleTogglePinned = async (topic: ForumTopic) => {
    try {
      await toggleTopicPinned(topic.id, !topic.isPinned);
      await loadTopics();
    } catch (error) {
      console.error('Error toggling pinned:', error);
      alert('Failed to toggle pinned status');
    }
  };

  const handleToggleLocked = async (topic: ForumTopic) => {
    try {
      await toggleTopicLocked(topic.id, !topic.isLocked);
      await loadTopics();
    } catch (error) {
      console.error('Error toggling locked:', error);
      alert('Failed to toggle locked status');
    }
  };

  const handleDelete = async (topic: ForumTopic) => {
    if (!confirm(`Are you sure you want to delete "${topic.title}"?`)) {
      return;
    }
    try {
      await deleteTopic(topic.id);
      await loadTopics();
    } catch (error) {
      console.error('Error deleting topic:', error);
      alert('Failed to delete topic');
    }
  };

  const handleOpenCreate = () => {
    setEditingTopic(null);
    setShowModal(true);
  };

  const handleOpenEdit = (topic: ForumTopic) => {
    setEditingTopic(topic);
    setShowModal(true);
  };

  const handleSave = async (data: {
    roomId?: string;
    roomName?: string;
    title: string;
    content?: string;
  }) => {
    const authorName = user?.displayName ?? user?.email?.split('@')[0] ?? 'Admin';

    if (editingTopic) {
      await updateTopic(editingTopic.id, { title: data.title });
    } else {
      await createTopic({
        roomId: data.roomId!,
        roomName: data.roomName!,
        title: data.title,
        content: data.content!,
        authorId: user!.uid,
        authorName,
      });
    }
    setShowModal(false);
    await loadTopics();
  };

  const formatDate = (value: unknown) => {
    // Firestore may return a Timestamp object (e.g. from serverTimestamp()) or an ISO string
    const date =
      value && typeof value === 'object' && 'toDate' in value
        ? (value as { toDate: () => Date }).toDate()
        : new Date(value as string);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="loading-spinner">
        <div className="spinner-border text-primary" role="status" />
      </div>
    );
  }

  return (
    <div className="page-header">
      <div className="row g-2 align-items-center">
        <div className="col">
          {isScoped ? (
            <>
              <div className="page-pretitle d-flex align-items-center gap-2">
                <button
                  className="btn btn-link p-0 text-muted"
                  onClick={() => navigate('/forum-rooms')}
                >
                  <IconArrowLeft size={16} className="me-1" />
                  Rooms
                </button>
                <span>/</span>
                <span>{scopedRoom?.name ?? scopedRoomId}</span>
              </div>
              <h2 className="page-title">Topics</h2>
            </>
          ) : (
            <>
              <div className="page-pretitle">Forum</div>
              <h2 className="page-title">Topics</h2>
            </>
          )}
        </div>
        <div className="col-auto">
          <button onClick={handleOpenCreate} className="btn btn-primary">
            <IconPlus className="icon" />
            Create Topic
          </button>
        </div>
      </div>

      {/* Filters — hidden in scoped mode */}
      {!isScoped && (
        <div className="row g-2 mt-3">
          <div className="col-md-4">
            <select
              className="form-select"
              value={roomFilter}
              onChange={e => setRoomFilter(e.target.value)}
            >
              <option value="">All Rooms</option>
              {rooms.map(room => (
                <option key={room.id} value={room.id}>
                  {room.name}
                </option>
              ))}
            </select>
          </div>
          <div className="col-md-8">
            <input
              type="text"
              className="form-control"
              placeholder="Search topics..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
            />
          </div>
        </div>
      )}

      {/* Topics Table */}
      <div className="card mt-3">
        <div className="table-responsive">
          <table className="table table-vcenter card-table">
            <thead>
              <tr>
                <th>Title</th>
                {!isScoped && <th>Room</th>}
                <th>Author</th>
                <th>Posts</th>
                <th>Views</th>
                <th>Last Activity</th>
                <th>Status</th>
                <th className="w-1">Actions</th>
              </tr>
            </thead>
            <tbody>
              {topics.length === 0 ? (
                <tr>
                  <td colSpan={isScoped ? 7 : 8} className="text-center text-muted py-4">
                    No topics found
                  </td>
                </tr>
              ) : (
                topics.map(topic => (
                  <tr key={topic.id}>
                    <td>
                      <div className="d-flex align-items-center">
                        {topic.isPinned && (
                          <IconPinFilled className="icon text-primary me-1" size={16} />
                        )}
                        {topic.isLocked && <IconLock className="icon text-muted me-1" size={16} />}
                        <span className="text-reset">{topic.title}</span>
                      </div>
                    </td>
                    {!isScoped && (
                      <td>
                        {topic.roomName ||
                          rooms.find(r => r.id === topic.roomId)?.name ||
                          topic.roomId}
                      </td>
                    )}
                    <td>{topic.authorName}</td>
                    <td>{topic.postCount}</td>
                    <td>{topic.viewCount}</td>
                    <td>{formatDate(topic.lastActivityAt)}</td>
                    <td>
                      <div className="d-flex gap-1">
                        {topic.isPinned && (
                          <span className="badge bg-primary text-white">Pinned</span>
                        )}
                        {topic.isLocked && (
                          <span className="badge bg-secondary text-white">Locked</span>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="btn-list flex-nowrap">
                        <button
                          onClick={() => navigate(`/forum-topics/${topic.id}/posts`)}
                          className="btn btn-icon"
                          title="View Posts"
                        >
                          <IconMessages className="icon" />
                        </button>
                        <button
                          onClick={() => handleTogglePinned(topic)}
                          className="btn btn-icon"
                          title={topic.isPinned ? 'Unpin' : 'Pin'}
                        >
                          {topic.isPinned ? (
                            <IconPinFilled className="icon" />
                          ) : (
                            <IconPin className="icon" />
                          )}
                        </button>
                        <button
                          onClick={() => handleToggleLocked(topic)}
                          className="btn btn-icon"
                          title={topic.isLocked ? 'Unlock' : 'Lock'}
                        >
                          {topic.isLocked ? (
                            <IconLock className="icon" />
                          ) : (
                            <IconLockOpen className="icon" />
                          )}
                        </button>
                        <button
                          onClick={() => handleOpenEdit(topic)}
                          className="btn btn-icon"
                          title="Edit"
                        >
                          <IconPencil className="icon" />
                        </button>
                        <button
                          onClick={() => handleDelete(topic)}
                          className="btn btn-icon text-danger"
                          title="Delete"
                        >
                          <IconTrash className="icon" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && (
        <TopicModal
          topic={editingTopic}
          fixedRoomId={isScoped ? scopedRoomId : undefined}
          fixedRoomName={isScoped ? (scopedRoom?.name ?? '') : undefined}
          onSave={handleSave}
          onClose={() => setShowModal(false)}
        />
      )}
    </div>
  );
}
